import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { LocationOnOutlined, GetApp } from '@material-ui/icons'

import { Button, Input, Pill } from '@baseV2'
import { displayAmount, allowNumericKeyDown } from '@utils'
import { Spacings, Text } from '@styles'

const Card = styled.div`
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.md};
	padding: ${Spacings.SPACING_3B};
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_2B};
	background: ${({ theme }) => theme.colors.surface.card};
`

const TopRow = styled.div`
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
	flex-wrap: wrap;
`

const InvoiceNo = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_700};
	color: ${({ theme }) => theme.colors.text.primary};
`

const RightMeta = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	flex-wrap: wrap;
`

const DueDate = styled.span`
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.secondary};
`

const DeliveryPill = styled.span`
	font-size: ${Text.EXTRA_SMALL};
	font-weight: ${Text.BOLD_600};
	text-transform: uppercase;
	letter-spacing: 0.02em;
	padding: ${Spacings.SPACING_1} ${Spacings.SPACING_2B};
	border-radius: ${({ theme }) => theme.radius.sm};
	color: ${({ theme, $delivered }) => ($delivered ? theme.colors.status.success.text : theme.colors.text.secondary)};
	background: transparent;
`

const DocLink = styled.span`
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.colors.brand.primary};
	cursor: pointer;

	&:hover {
		text-decoration: underline;
	}
`

const ShippingText = styled.div`
	display: flex;
	align-items: flex-start;
	gap: ${Spacings.SPACING_1B};
	font-size: ${Text.SMALL};
	line-height: ${Text.X_LARGE};
	color: ${({ theme }) => theme.colors.text.secondary};
`

// Tagged Amount on the left, Approve/Reject (or the status badge) on its right.
const TaggedRow = styled.div`
	display: flex;
	align-items: stretch;
	gap: ${Spacings.SPACING_2B};
	flex-wrap: wrap;
`

// Tagged-amount input: compact, bottom-aligned with the cell beside it (like the Add Payment field).
const AmountInputCell = styled.div`
	flex: 1 1 7rem;
	max-width: 10rem;
	min-width: 6rem;
	align-self: flex-end;
`

const RowActions = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	margin-left: auto;
	flex-wrap: wrap;
`

// Address on the left, Invoice Amount snug on the right (same as the Add Payment card).
const AddressRow = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_3B};
	flex-wrap: wrap;

	& > *:first-child {
		flex: 1 1 10rem;
		min-width: 0;
	}

	& > *:last-child {
		flex: 0 0 auto;
	}
`

// Balance Due + Invoice Amount, grouped on the right of the address. The pair always stays on
// one line (they size to their content); the whole group wraps under the address when space runs out.
const AddressRowStats = styled.div`
	display: flex;
	align-items: stretch;
	gap: ${Spacings.SPACING_2B};
	margin-left: auto;
	flex-wrap: nowrap;

	& > * {
		flex: 0 0 auto;
		min-width: 0;
		max-width: none;
		background: transparent;
		border: none;
		padding: 0;
	}
`

const Stat = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_1};
	// background: ${({ theme }) => theme.colors.surface.sunken};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.md};
	padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B};
`

const InlineStat = styled(Stat)`
	flex: 1 1 8rem;
	min-width: 7rem;
	max-width: 14rem;
	justify-content: center;
`

// Tagged Amount and its Approve/Reject actions share one cell: figures left, buttons right.
const TaggedAmountCell = styled(InlineStat)`
	flex: 1 1 16rem;
	max-width: none;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
	padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_1B} ${Spacings.SPACING_1B} ${Spacings.SPACING_3B};
`

const StatBody = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_1};
	min-width: 0;
`

const StatLabel = styled.span`
	font-size: ${Text.EXTRA_SMALL};
	color: ${({ theme }) => theme.colors.text.secondary};
`

const StatValue = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme, $highlight }) => ($highlight ? theme.colors.brand.primary : theme.colors.text.primary)};
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
`

// --- self-contained document preview modal (server sends attachment headers → fetch as blob) ---
const ModalOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 1000;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(0, 0, 0, 0.55);
	padding: ${Spacings.SPACING_4B};
`
const ModalCard = styled.div`
	background: ${({ theme }) => theme.colors.surface.card};
	border-radius: ${({ theme }) => theme.radius.md};
	width: min(900px, 100%);
	height: min(90vh, 100%);
	display: flex;
	flex-direction: column;
	overflow: hidden;
`
const ModalHead = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${Spacings.SPACING_2B};
	padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`
const ModalTitle = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_700};
	color: ${({ theme }) => theme.colors.text.primary};
`
const ModalClose = styled.button`
	border: none;
	background: transparent;
	font-size: ${Text.X_LARGE};
	line-height: 1;
	cursor: pointer;
	color: ${({ theme }) => theme.colors.text.secondary};
`
const ModalBody = styled.div`
	flex: 1;
	overflow: auto;
	background: ${({ theme }) => theme.colors.surface.sunken};
	display: flex;
	align-items: center;
	justify-content: center;

	& > iframe,
	& > img {
		width: 100%;
		height: 100%;
		border: none;
	}
	& > img {
		object-fit: contain;
	}
`
const ModalMessage = styled.div`
	padding: ${Spacings.SPACING_6B};
	text-align: center;
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_500};
	color: ${({ theme }) => theme.colors.text.secondary};
`

const isImageUrl = (url = '') => /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(url)

const inferMimeType = (url = '') => {
	const ext = (url.split(/[?#]/)[0].split('.').pop() || '').toLowerCase()
	const map = {
		pdf: 'application/pdf',
		png: 'image/png',
		jpg: 'image/jpeg',
		jpeg: 'image/jpeg',
		gif: 'image/gif',
		webp: 'image/webp',
		bmp: 'image/bmp',
		svg: 'image/svg+xml',
	}
	return map[ext] || 'application/pdf'
}

const InvoiceCard = ({
	invoice,
	statusBadge,
	actions = [],
	active = false,
	// Optional editable tagged amount (admins only — the container decides `amountDisabled`).
	showAmountInput = false,
	amountValue = '',
	amountDisabled = false,
	onAmountChange,
}) => {
	const [preview, setPreview] = useState(null) // { url, title } | null
	const [blobUrl, setBlobUrl] = useState(null)
	const [previewStatus, setPreviewStatus] = useState('idle') // idle | loading | ready | error

	useEffect(() => {
		if (!preview?.url) return undefined
		let objectUrl
		let cancelled = false
		setPreviewStatus('loading')
		setBlobUrl(null)
		fetch(preview.url)
			.then((res) => {
				if (!res.ok) throw new Error(`HTTP ${res.status}`)
				return res.blob()
			})
			.then((blob) => {
				if (cancelled) return
				const typed = new Blob([blob], { type: inferMimeType(preview.url) })
				objectUrl = URL.createObjectURL(typed)
				setBlobUrl(objectUrl)
				setPreviewStatus('ready')
			})
			.catch(() => {
				if (!cancelled) setPreviewStatus('error')
			})
		return () => {
			cancelled = true
			if (objectUrl) URL.revokeObjectURL(objectUrl)
		}
	}, [preview])

	if (!invoice) return null

	return (
		<Card $active={active}>
			<TopRow>
				<InvoiceNo>{invoice.number}</InvoiceNo>
				<RightMeta>
					{invoice.docUrl && (
						<DocLink
							role='button'
							tabIndex={0}
							title='Download document'
							onClick={() => setPreview({ url: invoice.docUrl, title: invoice.number })}
						>
							<GetApp fontSize='small' />
						</DocLink>
					)}
					{invoice.deliveryStatus && (
						<DeliveryPill $delivered={String(invoice.deliveryStatus).toLowerCase().includes('deliver')}>
							{invoice.deliveryStatus}
						</DeliveryPill>
					)}
					{invoice.dueDateLabel && <DueDate>Due: {invoice.dueDateLabel}</DueDate>}
				</RightMeta>
			</TopRow>

			<AddressRow>
				{invoice.shippingLocation ? (
					<ShippingText>
						<LocationOnOutlined style={{ fontSize: '1rem', flexShrink: 0 }} />
						<span>{invoice.shippingLocation}</span>
					</ShippingText>
				) : (
					<span />
				)}
				<AddressRowStats>
					{invoice.balanceDue != null && (
						<InlineStat>
							<StatLabel>Balance Due</StatLabel>
							<StatValue>{displayAmount(invoice.balanceDue)}</StatValue>
						</InlineStat>
					)}
					<InlineStat>
						<StatLabel>Invoice Amount</StatLabel>
						<StatValue>{displayAmount(invoice.invoiceAmount)}</StatValue>
					</InlineStat>
				</AddressRowStats>
			</AddressRow>

			{(invoice.taggedAmount != null || actions.length > 0 || statusBadge || showAmountInput) && (
				<TaggedRow>
					{showAmountInput && (
						<AmountInputCell>
							<Input
								type='number'
								min='0'
								placeholder='0'
								leftIcon='₹'
								value={amountValue ?? ''}
								disabled={amountDisabled}
								onKeyDown={allowNumericKeyDown}
								onChange={(e) => onAmountChange?.(invoice, e.target.value)}
							/>
						</AmountInputCell>
					)}
					<TaggedAmountCell>
						<StatBody>
							<StatLabel>Tagged Amount</StatLabel>
							<StatValue $highlight>{displayAmount(invoice.taggedAmount ?? 0)}</StatValue>
						</StatBody>
						<RowActions>
							{actions.length > 0
								? actions.map((a) => (
										<Button
											key={a.label}
											size='small'
											color={a.color || 'primary'}
											variant={a.variant || 'solid'}
											disabled={a.disabled}
											title={a.title}
											onClick={a.onClick}
										>
											{a.label}
										</Button>
								  ))
								: statusBadge && (
										<Pill color={statusBadge.color} size='small'>
											{statusBadge.label}
										</Pill>
								  )}
						</RowActions>
					</TaggedAmountCell>
				</TaggedRow>
			)}

			{preview && (
				<ModalOverlay onClick={() => setPreview(null)}>
					<ModalCard onClick={(e) => e.stopPropagation()}>
						<ModalHead>
							<ModalTitle>{preview.title || 'Document'}</ModalTitle>
							<ModalClose type='button' aria-label='Close preview' onClick={() => setPreview(null)}>
								×
							</ModalClose>
						</ModalHead>
						<ModalBody>
							{previewStatus === 'loading' && <ModalMessage>Loading preview…</ModalMessage>}
							{previewStatus === 'error' && <ModalMessage>Preview not available for this document.</ModalMessage>}
							{previewStatus === 'ready' &&
								blobUrl &&
								(isImageUrl(preview.url) ? (
									<img src={blobUrl} alt={preview.title || 'Document preview'} />
								) : (
									<iframe src={blobUrl} title={preview.title || 'Document preview'} />
								))}
						</ModalBody>
					</ModalCard>
				</ModalOverlay>
			)}
		</Card>
	)
}

export default InvoiceCard
